import React, { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart, ArcElement, Tooltip, Legend as ChartLegend } from "chart.js";

Chart.register(ArcElement, Tooltip, ChartLegend);

const FlightChart = ({ lv1, lv2, lv3, lv4, lv5, area1, area2, area3, area4, area5, chartType }) => {
  const currentTotal = useMemo(() => {
    return (lv1 || 0) + (lv2 || 0) + (lv3 || 0) + (lv4 || 0) + (lv5 || 0);
  }, [lv1, lv2, lv3, lv4, lv5]);

  const segments = useMemo(() => {
    const formatArea = (area) =>
      area ? `${area.toFixed(2)} acres` : "0 acres";

    if (chartType === "acres_by_pilot") {
      return [
        { label: "0 - 1 Acre", value: lv1 || 0, color: "#F2726F", area: area1 },
        { label: "1 - 2 Acre", value: lv2 || 0, color: "#FFC533", area: area2 },
        { label: "2 - 3 Acre", value: lv3 || 0, color: "#62B58F", area: area3 },
        { label: "3 - 4 Acre", value: lv4 || 0, color: "#3988F9", area: area4 },
        { label: "4+ Acre", value: lv5 || 0, color: "#8E44AD", area: area5 },
      ];
    }
    return [
      { label: "0 - 0.3 Acre", value: lv1 || 0, color: "#F2726F", area: area1 }, 
      { label: "0.31 - 0.6 Acre", value: lv2 || 0, color: "#FFC533", area: area2 }, 
      { label: "0.61 - 0.9 Acre", value: lv3 || 0, color: "#62B58F", area: area3 }, 
      { label: "0.91 - 5 Acre", value: lv4 || 0, color: "#3988F9", area: area4 }, 
      { label: "5.01 Acre & Above", value: lv5 || 0, color: "#8E44AD", area: area5 },
    ];
  }, [lv1, lv2, lv3, lv4, lv5, area1, area2, area3, area4, area5, chartType]);

  const allValuesZero = segments.every((s) => s.value === 0);

  const data = {
    labels: segments.map((s) => s.label),
    datasets: [
      {
        data: segments.map((s) => s.value),
        backgroundColor: segments.map((s) => s.color),
        borderWidth: 2,
        borderColor: "#ffffff",
      },
    ],
  };

  const centerTextPlugin = {
    id: "centerText",
    beforeDraw: (chart) => {
      const { width, height, ctx } = chart;
      ctx.save();

      const fontSizeBase = Math.min(width, height) / 200;

      const mainText = "Total Flights";
      const subText = currentTotal.toString();

      ctx.font = `${Math.max(fontSizeBase * 11, 12)}px Arial, sans-serif`;
      ctx.fillStyle = "#1f2937";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const centerX = width / 2;
      const centerY = height / 2;

      ctx.fillText(mainText, centerX, centerY - 14);

      ctx.font = `bold ${Math.max(fontSizeBase * 18, 16)}px Arial, sans-serif`;
      ctx.fillStyle = "#1f2937";
      ctx.fillText(subText, centerX, centerY + 14);

      ctx.restore();
    },
  };

  const options = {
    cutout: "70%",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed || 0;
            return `${label}: ${value} flights`;
          },
        },
      },
    },
  };

  const legendData = [...segments];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        height: "100%",
        gap: "20px",
      }}
    >
      <div
        style={{
          width: "50%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {allValuesZero ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#6b7280", fontSize: "1rem", textAlign: "center", }} >
            <div>No flight data available</div>
          </div>
        ) : (
          <Doughnut
            data={data}
            options={options}
            plugins={[centerTextPlugin]}
            key={currentTotal}
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          />
        )}
      </div>

      <div
        style={{
          width: "50%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "14px",
          fontWeight: "500",
          color: "#333",
          textAlign: "center",
          wordWrap: "break-word",
          overflowWrap: "break-word",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%", }} >
          {legendData.map((item, index) => (
            <div
              key={index}
              style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", opacity: item.value > 0 ? 1 : 0.6, }} >
              <span
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "50%",
                  backgroundColor: item.color,
                  flexShrink: 0,
                  marginTop: "4px",
                }}
              />
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: "600", color: "#1f2937" }}>
                  {item.value} Flights
                </div>
                <div style={{ color: "#6b7280" }}>
                  ({item.label}) - {Math.round(item.area) || "0"} Acre
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FlightChart;